import { useContext, useState, useEffect } from "react";
import OutputWindow from "../../../components/ui/output_window/OutputWindow";
import styles from "./styles/PosResult.module.css";

import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import DataContext from "../../../context/DataContext";
import { axiosSW } from "../../../api/axiosDefaults";
import Spinner from "../../../components/ui/spinner/Spinner";
import NavButton from "../../../components/ui/navbutton/NavButton";
import POSDataContext from "../../../context/POSDataContext";
import Button from "../../../components/ui/button/Button";
import { ErrorButtons } from "../../../components/error_result_buttons/ErrorButtons";
import AuthContext from "../../../context/AuthContext";
import { capitalizeFirstLetter } from "../../../utils/utils";

/**
 * The Result component
 * Used to display the result for searched ticket or processed tickets
 */
const PosResult = () => {
    const [isMinorError, setIsMinorError] = useState(false);
    const [notReadyView, setNotReadyView] = useState(true);

    const {
        currentCustom, // {Object} - The current client customisathions
        currentBU, // {String} - The current business user
        isServiceDataAvailable, // {Boolean} - Indicates if the service data is available
        serviceData, // {Function} - Fetch the service data
        homePath, // {String} - The path to the home page
        connection, // {Object} - The current client settings
        isServerDown,
        result,
        isLoading,
        JWTToken,
    } = useContext(DataContext);
    const {
        resultPos, // {Object} - The result data for the POS module
        isLoadingPos, // {Boolean} - Indicates if data is loading in the POS module
        handleErrorPos, // {Function} - Function to handle errors in POS module
        setResultPos,
    } = useContext(POSDataContext);
    const { user } = useContext(AuthContext);
    const { bUser } = useParams();
    const navigate = useNavigate();

    const [inProcess, setInProcess] = useState(false);
    const { t } = useTranslation();

    // Redirect to main page if no service data or if no result object avialable
    useEffect(() => {
        if (!resultPos && !result) {
            navigate(`/${bUser}/pos`);
        } else if (!resultPos && result) {
            setResultPos(result);
        }
    }, [bUser]);

    // Fetch service data if not avialable or the Busness user changes
    // IMPORTANT!!!!   when the server is down an error occures (not possible to get the service data), the serviceData function will redirect one more time to the result page. this will create a loop.
    // a counter should be used to prevent this, and the error before the loop should be saved to be used in the log
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            !isServerDown &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // Check ready for viewing
    useEffect(() => {
        if (isServiceDataAvailable && (!isLoadingPos || !isLoading)) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoadingPos, isLoading]);

    useEffect(() => {
        if (resultPos?.error?.message === "Failed reCAPTCHA verification") {
            setIsMinorError(true);
        }
    }, [resultPos?.error?.message]);

    /**
     * Create the email content object
     * @returns {Object} the email content object
     */
    const getEmailContent = () => {
        if (resultPos?.error) {
            return {
                email: currentCustom?.supportEmail,
                subject: resultPos?.errorMessage
                    ? resultPos?.errorMessage
                    : "Unknown Error",
                body: resultPos?.error,
            };
        }
    };

    /**
     * The result feed back content for the window
     * The header and the message body changes depending on the result object
     * @returns {JSX.Element} - The JSX element to be rendered.
     */
    const getWindowContent = () => {
        let header = "";
        let message = "";

        if (resultPos) {
            if (resultPos?.error) {
                if (
                    resultPos?.error?.message ===
                    "Failed reCAPTCHA verification"
                ) {
                    header = t(`reCaptcha error`);
                    message = t(`please try again later.`);
                } else if (
                    resultPos?.error?.code === "Network Error" ||
                    resultPos?.error?.request?.status === 0
                ) {
                    header = t(`server down`);
                    message = t(`please try again later.`);
                } else if (
                    resultPos?.error?.response?.data?.outputStatus === 8 ||
                    resultPos?.error?.outputStatus === 8
                ) {
                    header = t(`unhandled request`);
                    message = t(`unhandled request message`);
                } else if (
                    resultPos?.error?.response?.data?.outputStatus === 0 ||
                    resultPos?.error?.outputStatus === 0
                ) {
                    header = t(`unknown error`);
                    message = t(`unknown error message`);
                } else {
                    header = t(`technical error`)
                        ? t(`technical error`)
                        : "Technical error"; // Hard coded in case no localization file exist
                    message = t(`technical error message`)
                        ? t(`technical error message`)
                        : "Please try again later."; // Hard coded in case no localization file exist
                }
            } else if (resultPos?.outputStatus === 1) {
                header = t(`ticket not found`);
                message = t(`ticket not found message`);
            } else if (resultPos?.outputStatus === 2) {
                header = t(`ticket reserved`);
                message = t(`ticket reserved message`);
            } else if (resultPos?.outputStatus === 5) {
                header = t(`settlement reserved`);
                message = t(`settlement reserved message`);
            } else if (resultPos?.outputStatus === 9) {
                header = t(`ticket in process`);
                message = t(`ticket in process message`);
            } else if (resultPos?.outputStatus === 3) {
                header = t(`ticket paid pos`);
                message = resultPos?.referenceNumber
                    ? `${t("receipt number")} : ${resultPos?.referenceNumber}`
                    : t(`ticket paid pos message`);
            } else if (resultPos?.outputStatus === 6) {
                header = t(`settlement paid pos`);
                message = resultPos?.referenceNumber
                    ? `${t("receipt number")} : ${resultPos?.referenceNumber}`
                    : t(`settlement paid pos message`);
            } else if (resultPos?.outputStatus === 101) {
                header = t(`settlements/tickets could not be added`);
                message = t(`settlements/tickets could not be added message`);
            } else if (resultPos?.transactionNumber) {
                header = t(`payment was successful`);
                message = `${t("receipt number")} : ${
                    resultPos?.transactionNumber
                }`;
            } else {
                header = t(`unhandled request or error`);
                message = t(`unhandled request or error message`);
            }

            return (
                <>
                    {inProcess ? (
                        <div
                            className={`${styles.result_spinner_container} flex-container`}
                        >
                            <Spinner />
                        </div>
                    ) : (
                        <div
                            className={`${styles.result_text_container} v-flex-container`}
                        >
                            <h1>{header}</h1>

                            <p>{capitalizeFirstLetter(message)}</p>
                        </div>
                    )}
                </>
            );
        }
    };

    /**
     * Submit handle
     * Fetch the data and open the pdf in a new tab with the print dialog
     */
    const handlePrint = async () => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            referenceNo:
                resultPos?.transactionNumber || resultPos?.referenceNumber,
        };
        try {
            setInProcess(true);
            const { data } = await axiosSW.post(
                "PaymentReceipt/pos/GetPaymentReceipt",
                payLoad,
            );

            if (data) {
                const base64PDF = data.fileContent;
                const binaryString = window.atob(base64PDF);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);

                // Open the PDF in a new tab
                const pdfWindow = window.open(url, "_blank");
                if (pdfWindow) {
                    // Trigger the print dialog after the PDF is loaded
                    pdfWindow.addEventListener("load", () => {
                        pdfWindow.print();
                    });
                }
                setInProcess(false);
            }
        } catch (error) {
            handleErrorPos(error);
        }
    };

    const goBack = () => {
        navigate(-1);
    };

    return (
        <article className={`  main-page-container flex-container`}>
            {isLoadingPos || (!isServerDown && !isServiceDataAvailable) ? (
                <Spinner />
            ) : (
                <section
                    className={` ${
                        styles.main_result_page_container
                    } transition-container 
                    ${
                        notReadyView
                            ? "transition-hidden"
                            : "transition-visible"
                    }  main-container flex-container`}
                >
                    <div
                        className={`${styles.result_main_container} v-flex-container`}
                    >
                        <OutputWindow
                            color={
                                !resultPos?.error
                                    ? currentCustom?.color?.current
                                    : currentCustom?.color?.error
                            }
                            content={getWindowContent()}
                        />

                        {resultPos?.error ? (
                            <ErrorButtons isMinorError={isMinorError} />
                        ) : resultPos?.outputStatus === 101 ? (
                            <>
                                <div
                                    className={`${styles.result_buttons_container} flex-container`}
                                >
                                    <div>
                                        <Button
                                            body={t(`go back`)}
                                            type={"button"}
                                            onClickFuction={goBack}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div
                                className={`${styles.result_buttons_container} flex-container`}
                            >
                                {resultPos?.transactionNumber ||
                                resultPos?.referenceNumber ? (
                                    <div>
                                        <Button
                                            body={t(`print`)}
                                            type={"button"}
                                            disableHTML={inProcess}
                                            disabled={inProcess}
                                            onClickFuction={handlePrint}
                                        />
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <div>
                                    <NavButton
                                        body={t(`back to search`)}
                                        link={`${homePath}/tickets`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </article>
    );
};

export default PosResult;
